<template>
  <b-card class="legacy-bundle-import-form">
    <h5 class="mb-2">
      <i class="fas fa-database mr-2 text-primary"></i>
      นำเข้าข้อมูลจากโปรแกรมเดิม (FD7 bundle)
    </h5>
    <p class="text-muted small mb-3">
      Imports patients, procedures, products and treatment history from a bundle.json made from the
      clinic's old database. Each type is checked first (nothing written), then imported. Running it
      again is safe: anything already imported is skipped. Doctor fee on imported procedures and
      products: {{ doctorFeePercent }}%.
    </p>

    <ClinicSearchSelect v-model="selectedClinic" label="Select Clinic *" :disabled="running" />

    <b-form-group label="Select Branch *" class="mt-3">
      <b-form-select v-model="selectedBranchId" :options="branchOptions" :disabled="!selectedClinic || running">
        <template #first>
          <b-form-select-option :value="null" disabled>
            {{ selectedClinic ? 'Choose a branch...' : 'Select a clinic first' }}
          </b-form-select-option>
        </template>
      </b-form-select>
    </b-form-group>

    <div v-if="statusLoading" class="text-muted small"><i class="fas fa-spinner fa-spin mr-1"></i>Reading branch…</div>

    <div v-if="status" class="mb-3">
      <b-table-simple small class="mb-2">
        <b-tbody>
          <b-tr><b-td>Target</b-td><b-td class="text-right font-weight-bold">{{ targetName }}</b-td></b-tr>
          <b-tr><b-td>Clinic type</b-td><b-td class="text-right">{{ status.clinic.type }}</b-td></b-tr>
          <b-tr><b-td>Patients in branch</b-td><b-td class="text-right">{{ fmt(status.counts.patients) }}</b-td></b-tr>
          <b-tr><b-td>…of which imported</b-td><b-td class="text-right">{{ fmt(status.counts.importedPatients) }}</b-td></b-tr>
          <b-tr><b-td>Procedures</b-td><b-td class="text-right">{{ fmt(status.counts.procedures) }}</b-td></b-tr>
          <b-tr><b-td>Products and drugs</b-td><b-td class="text-right">{{ fmt(status.counts.items) }}</b-td></b-tr>
          <b-tr><b-td>Imported visits</b-td><b-td class="text-right">{{ fmt(status.counts.importedVisits) }}</b-td></b-tr>
        </b-tbody>
      </b-table-simple>

      <b-form-group label="Room for imported visits *">
        <b-form-select v-model="roomId" :options="roomOptions" :disabled="running" />
      </b-form-group>
    </div>

    <b-form-group label="bundle.json *">
      <b-form-file
        v-model="bundleFile"
        accept=".json,application/json"
        placeholder="Choose bundle.json…"
        :disabled="running"
        @input="readBundle"
      />
    </b-form-group>
    <div v-if="bundleReading" class="text-muted small"><i class="fas fa-spinner fa-spin mr-1"></i>Reading file…</div>
    <b-alert v-if="bundleError" variant="danger" show>{{ bundleError }}</b-alert>
    <div v-if="bundle" class="small text-muted mb-2">
      Bundle: {{ fmt(bundle.patients.length) }} patients, {{ fmt(bundle.procedures.length) }} procedures,
      {{ fmt(bundle.items.length) }} products, {{ fmt(bundle.history.length) }} visits
    </div>

    <template v-if="checks && !complete">
      <b-alert v-if="checks.resuming" variant="info" show>
        An earlier import into this branch was found. Running again continues it; nothing is duplicated.
      </b-alert>
      <b-alert v-for="(text, i) in checks.blockers" :key="'b' + i" variant="danger" show>{{ text }}</b-alert>
      <b-alert v-for="(text, i) in checks.warnings" :key="'w' + i" variant="warning" show>{{ text }}</b-alert>
    </template>

    <b-button variant="primary" block :disabled="!canImport" @click="startImport">
      <i class="fas mr-2" :class="running ? 'fa-spinner fa-spin' : complete ? 'fa-check' : 'fa-file-import'"></i>
      {{ buttonLabel }}
    </b-button>
    <div v-if="running" class="small text-muted mt-1">Stay on this page until it finishes.</div>
    <div v-else-if="cooldown > 0" class="small text-muted mt-1">
      Waiting {{ cooldown }} s before the next try, in case the server is still finishing the last part.
    </div>

    <div v-if="progress.length" class="mt-3">
      <div v-for="row in progress" :key="row.kind" class="mb-2">
        <div class="d-flex justify-content-between small">
          <span>{{ kindLabels[row.kind] }}</span>
          <span>{{ row.text }}</span>
        </div>
        <b-progress :value="row.done" :max="row.total || 1" height="6px" :variant="row.variant" />
      </div>
    </div>

    <b-alert v-if="failure" variant="danger" show class="mt-3">
      <div>{{ failure.message }}</div>
      <pre v-if="failure.details" class="mt-2 mb-0 small legacy-import-details">{{ failure.details }}</pre>
    </b-alert>

    <div v-if="showResults" class="mt-3">
      <b-alert v-if="bundle && complete" variant="success" show>
        <i class="fas fa-check-circle mr-1"></i>
        All checks passed. The import is complete and the branch is ready to use.
      </b-alert>
      <b-alert v-else-if="failedChecks > 0" variant="danger" show>
        {{ failedChecks }} safety check(s) failed. Do not hand this branch to the clinic yet; send a screenshot of
        this table to the developer.
      </b-alert>
      <b-alert v-else-if="bundle && unfinished" variant="warning" show>
        The import is not finished yet. Press "นำเข้าต่อ (continue import)" to import the rest.
      </b-alert>
      <h6>ผลตรวจสอบหลังนำเข้า</h6>
      <b-table-simple small>
        <b-tbody>
          <b-tr v-for="row in comparison" :key="row.label">
            <b-td>{{ row.label }}</b-td>
            <b-td class="text-right">{{ fmt(row.actual) }} / {{ fmt(row.expected) }}</b-td>
            <b-td class="text-center"><i :class="row.ok ? 'fas fa-check text-success' : 'fas fa-times text-danger'"></i></b-td>
          </b-tr>
          <b-tr v-for="(value, key) in status.checks" :key="key">
            <b-td>{{ checkLabels[key] || key }}</b-td>
            <b-td class="text-right">{{ fmt(value) }}</b-td>
            <b-td class="text-center"><i :class="value === 0 ? 'fas fa-check text-success' : 'fas fa-times text-danger'"></i></b-td>
          </b-tr>
        </b-tbody>
      </b-table-simple>
    </div>

    <b-button v-if="status && !running" variant="link" size="sm" class="p-0 mt-2" @click="loadStatus">
      Refresh status
    </b-button>
  </b-card>
</template>

<script>
import SuperadminService from '@/services/SuperadminService';
import ClinicSearchSelect from './ClinicSearchSelect.vue';
import {
  IMPORT_KINDS,
  chunksFor,
  mergeReports,
  bundleProblem,
  preflight,
  compareCounts,
  importComplete
} from '@/utils/legacyImport';

// The doctor fee the clinic chose for imported procedures and products.
const DOCTOR_FEE_PERCENT = 50;
// API Gateway gives up after 29 s but the server (Lambda timeout 120 s, plus
// start-up) can keep committing, so a retry waits 150 s from the last request
// or it could write the same part twice.
const RETRY_WAIT_MS = 150 * 1000;
// When the last import request was sent, kept across page changes, reloads and
// tabs so a returning operator also waits.
const LAST_REQUEST_KEY = 'legacyImport.lastRequestAt';

// Why the import stopped, shown to the operator.
class ImportStop extends Error {
  constructor(message, { details = null, retryLater = false } = {}) {
    super(message);
    this.details = details;
    this.retryLater = retryLater;
  }
}

const readLastRequest = () => {
  try {
    return Number(localStorage.getItem(LAST_REQUEST_KEY)) || 0;
  } catch (error) {
    return 0;
  }
};
const writeLastRequest = () => {
  try {
    localStorage.setItem(LAST_REQUEST_KEY, String(Date.now()));
  } catch (error) {
    // Storage unavailable: the in-page cooldown still applies.
  }
};

export default {
  name: 'LegacyBundleImportForm',
  components: { ClinicSearchSelect },
  data() {
    return {
      doctorFeePercent: DOCTOR_FEE_PERCENT,
      selectedClinic: null,
      selectedBranchId: null,
      status: null,
      statusLoading: false,
      statusRequest: 0,
      roomId: null,
      bundleFile: null,
      bundle: null,
      bundleReading: false,
      bundleError: null,
      running: false,
      aborted: false,
      progress: [],
      failure: null,
      cooldown: 0,
      cooldownTimer: null,
      kindLabels: {
        patients: 'Patients',
        procedures: 'Procedures',
        items: 'Products and drugs',
        history: 'Treatment history'
      },
      checkLabels: {
        duplicateVisits: 'Duplicate visits',
        duplicateLegacyDns: 'Duplicate old patient numbers',
        duplicateDns: 'Duplicate patient numbers',
        incompleteVisits: 'Visits not completed or missing treatment',
        overdueItems: 'History items showing as owed',
        receiptLines: 'History items on a receipt',
        badSurfaces: 'Invalid tooth surfaces'
      }
    };
  },
  computed: {
    clinicId() {
      return this.selectedClinic ? this.selectedClinic.clinicId || this.selectedClinic.id : null;
    },
    branchOptions() {
      if (!this.selectedClinic || !this.selectedClinic.branches) return [];
      return this.selectedClinic.branches.map(branch => ({
        value: branch.branch_id || branch.branchId,
        text: `${branch.branch_code || branch.branchCode} - ${branch.branch_name_th || branch.branchNameTh}`
      }));
    },
    roomOptions() {
      return this.status ? this.status.rooms.map(room => ({ value: room.id, text: room.name })) : [];
    },
    targetName() {
      if (!this.status) return '';
      const { clinic, branch } = this.status;
      return `${clinic.name} / ${branch.name || 'branch'} (branch id ${branch.id})`;
    },
    statusMatchesSelection() {
      return Boolean(this.status && this.status.branch.id === Number(this.selectedBranchId));
    },
    checks() {
      return this.statusMatchesSelection && this.bundle ? preflight(this.status, this.bundle) : null;
    },
    complete() {
      return Boolean(this.statusMatchesSelection && this.bundle && importComplete(this.status, this.bundle));
    },
    canImport() {
      return Boolean(
        !this.running &&
          this.cooldown === 0 &&
          this.statusMatchesSelection &&
          this.bundle &&
          this.roomId &&
          this.checks &&
          this.checks.blockers.length === 0 &&
          !this.complete
      );
    },
    buttonLabel() {
      if (this.running) return 'Importing…';
      if (this.complete) return 'Import complete';
      return this.checks && this.checks.resuming ? 'นำเข้าต่อ (continue import)' : 'นำเข้า (import)';
    },
    showResults() {
      if (!this.statusMatchesSelection || this.running) return false;
      const { importedPatients, importedVisits } = this.status.counts;
      return importedPatients > 0 || importedVisits > 0;
    },
    comparison() {
      return this.statusMatchesSelection && this.bundle ? compareCounts(this.status.counts, this.bundle) : [];
    },
    failedChecks() {
      if (!this.statusMatchesSelection) return 0;
      return Object.values(this.status.checks).filter(value => value !== 0).length;
    },
    unfinished() {
      // Fewer rows than the bundle and no safety problem: a stopped import, not a broken one.
      return this.comparison.some(row => row.actual < row.expected);
    }
  },
  watch: {
    selectedClinic() {
      this.selectedBranchId = null;
      this.status = null;
    },
    selectedBranchId(value) {
      this.status = null;
      this.failure = null;
      this.progress = [];
      if (value) this.loadStatus();
    }
  },
  created() {
    this.resumeCooldown();
  },
  beforeDestroy() {
    // Leaving the page stops the loop before its next request; the request in
    // flight may still commit, which the stored timestamp makes the next visit wait for.
    this.aborted = true;
    this.stopCooldown();
    window.removeEventListener('beforeunload', this.confirmLeave);
    if (this.running) this.$emit('running', false);
  },
  methods: {
    fmt(value) {
      return Number(value || 0).toLocaleString('en-US');
    },
    async loadStatus() {
      const request = ++this.statusRequest;
      const branchId = Number(this.selectedBranchId);
      this.statusLoading = true;
      try {
        const response = await SuperadminService.getLegacyImportStatus(this.clinicId, branchId);
        // The operator may have picked another branch while this was loading.
        if (request !== this.statusRequest || branchId !== Number(this.selectedBranchId)) return;
        this.status = response.data.data;
        const roomIds = this.status.rooms.map(room => room.id);
        if (!roomIds.includes(this.roomId)) this.roomId = roomIds[0] || null;
      } catch (error) {
        if (request !== this.statusRequest) return;
        this.status = null;
        this.toastError((error.response && error.response.data && error.response.data.error) || 'Could not read the branch status');
      } finally {
        if (request === this.statusRequest) this.statusLoading = false;
      }
    },
    readBundle(file) {
      this.bundle = null;
      this.bundleError = null;
      if (!file) return;
      this.bundleReading = true;
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const parsed = JSON.parse(reader.result);
          const problem = bundleProblem(parsed);
          if (problem) throw new Error(problem);
          // Tens of thousands of records: keep Vue from making them reactive.
          this.bundle = Object.freeze(parsed);
        } catch (error) {
          this.bundleError = `Cannot use this file: ${error.message}`;
        } finally {
          this.bundleReading = false;
        }
      };
      reader.onerror = () => {
        this.bundleError = 'Cannot read this file';
        this.bundleReading = false;
      };
      reader.readAsText(file);
    },
    async startImport() {
      const message =
        `Import into ${this.targetName}? This writes ${this.fmt(this.bundle.patients.length)} patients and ` +
        `${this.fmt(this.bundle.history.length)} visits into the live database and cannot be undone from this page.`;
      const confirmed = await this.$bvModal.msgBoxConfirm(message, {
        title: 'Confirm import',
        okTitle: 'Import',
        okVariant: 'danger',
        cancelTitle: 'Cancel',
        centered: true
      });
      if (confirmed) await this.runImport();
    },
    async runImport() {
      this.running = true;
      this.aborted = false;
      this.failure = null;
      this.$emit('running', true);
      window.addEventListener('beforeunload', this.confirmLeave);

      const base = {
        clinicId: Number(this.clinicId),
        branchId: Number(this.selectedBranchId),
        df: DOCTOR_FEE_PERCENT
      };
      this.progress = IMPORT_KINDS.map(kind => ({ kind, done: 0, total: 0, text: 'Waiting', variant: 'secondary' }));
      let current = null;

      try {
        for (const [index, kind] of IMPORT_KINDS.entries()) {
          current = this.progress[index];
          const envelope = { ...base, kind, ...(kind === 'history' ? { roomId: this.roomId } : {}) };
          const chunks = chunksFor({ ...envelope, dryRun: false }, this.bundle[kind]);

          // Check every part before writing any of this type.
          await this.sendAll(current, envelope, chunks, true, index);
          const report = await this.sendAll(current, envelope, chunks, false, index);

          current.variant = 'success';
          current.text = this.summary(report);
        }
      } catch (error) {
        const stop = error instanceof ImportStop ? error : new ImportStop(`The import stopped: ${error.message}`);
        if (current) current.variant = 'danger';
        this.failure = { message: stop.message, details: stop.details };
        if (stop.retryLater) this.resumeCooldown();
      } finally {
        this.running = false;
        this.$emit('running', false);
        window.removeEventListener('beforeunload', this.confirmLeave);
      }
      if (!this.aborted) await this.loadStatus();
    },
    async sendAll(row, envelope, chunks, dryRun, kindIndex) {
      let total = null;
      let offset = 0;
      row.total = chunks.length;
      row.done = 0;
      row.variant = dryRun ? 'info' : 'primary';
      for (const [index, records] of chunks.entries()) {
        row.text = `${dryRun ? 'Checking' : 'Importing'} ${index + 1}/${chunks.length}`;
        const where = { kind: row.kind, part: index + 1, parts: chunks.length, dryRun, kindIndex, offset };
        const data = await this.send({ ...envelope, dryRun, records }, where);
        if (data.refused) throw this.refusal(where, data);
        total = mergeReports(total, data);
        row.done = index + 1;
        offset += records.length;
      }
      return total || {};
    },
    async send(body, where) {
      if (this.aborted) throw new ImportStop('The import stopped because you left the page. Open it again and press the button to continue.');
      writeLastRequest();
      let response;
      try {
        response = await SuperadminService.postLegacyImport(body);
      } catch (error) {
        throw this.requestFailure(error, where);
      }
      const data = response.data && response.data.data;
      if (!data || typeof data !== 'object') {
        throw new ImportStop(
          `${this.partName(where)}: the server sent an unexpected reply. Wait for the countdown, then press the button again.`,
          { retryLater: true }
        );
      }
      return data;
    },
    partName(where) {
      return `${this.kindLabels[where.kind]}, part ${where.part} of ${where.parts}`;
    },
    alreadyWritten(where) {
      const earlierKinds = IMPORT_KINDS.slice(0, where.kindIndex).map(kind => this.kindLabels[kind]);
      const earlierParts =
        !where.dryRun && where.part > 1 ? [`parts 1–${where.part - 1} of ${this.kindLabels[where.kind]}`] : [];
      const written = [...earlierKinds, ...earlierParts];
      return written.length > 0 ? `Already imported: ${written.join(', ')}.` : 'Nothing has been imported yet.';
    },
    refusal(where, report) {
      // Record numbers count from the start of this type in the bundle, not from the part.
      const errors = (report.errors || []).map(({ index, ...rest }) => ({ record: where.offset + index + 1, ...rest }));
      const details = JSON.stringify({ errors, conflicts: report.conflicts, unresolved: report.unresolved }, null, 2);
      return new ImportStop(
        `${this.partName(where)} was refused, so this part was not written. ${this.alreadyWritten(where)} ` +
          'Do not press the button again; send a screenshot of this box to the developer.',
        { details }
      );
    },
    requestFailure(error, where) {
      const status = error.response ? error.response.status : null;
      const reply = error.response ? error.response.data : null;
      if (status === 409 && reply && reply.data) return this.refusal(where, reply.data);
      if (!status || status >= 500) {
        return new ImportStop(
          `${this.partName(where)}: the server did not answer properly${status ? ` (HTTP ${status})` : ''}. ` +
            'It may still be finishing this part. Wait for the countdown, then press the button again; it continues where it stopped.',
          { retryLater: true }
        );
      }
      return new ImportStop(
        `${this.partName(where)}: ${(reply && reply.error) || `HTTP ${status}`}. ${this.alreadyWritten(where)} ` +
          'Send a screenshot of this box to the developer.'
      );
    },
    summary(report) {
      const imported = report.imported || 0;
      const skipped = Array.isArray(report.alreadyImported) ? report.alreadyImported.length : 0;
      return skipped > 0 ? `Done: ${this.fmt(imported)} new, ${this.fmt(skipped)} already there` : `Done: ${this.fmt(imported)}`;
    },
    resumeCooldown() {
      const until = readLastRequest() + RETRY_WAIT_MS;
      if (until <= Date.now()) return;
      this.stopCooldown();
      const tick = () => {
        this.cooldown = Math.max(0, Math.ceil((until - Date.now()) / 1000));
        if (this.cooldown === 0) this.stopCooldown();
      };
      tick();
      this.cooldownTimer = setInterval(tick, 1000);
    },
    stopCooldown() {
      if (this.cooldownTimer) clearInterval(this.cooldownTimer);
      this.cooldownTimer = null;
    },
    confirmLeave(event) {
      event.preventDefault();
      event.returnValue = '';
    },
    toastError(message) {
      this.$bvToast.toast(message, { title: 'Error', variant: 'danger', autoHideDelay: 5000 });
    }
  }
};
</script>

<style scoped>
.legacy-import-details {
  max-height: 240px;
  overflow: auto;
  white-space: pre-wrap;
}
</style>
